export type CourseLesson = {
  title: string;
  slug: string;
  subtitle: string;
  objectives: string[];
  locked?: boolean;
  pdfPath?: string;
};

export type CourseSection = {
  title: string;
  slug: string;
  lessons: CourseLesson[];
  locked?: boolean;
};

export type Course = {
  id: string;
  title: string;
  slug: string;
  language: "python" | "javascript";
  locked?: boolean;
  course: CourseSection[];
};
export const COURSES: Course[] = [
  {
    id: import.meta.env.VITE_COURSE_ID,
    title: "DP Python Programming",
    slug: "dp-python-programming",
    language: "python",
    course: [
      {
        title: "Python Fundamentals",
        slug: "python-fundamentals",
        lessons: [
          {
            title: "Variables and Data Types",
            slug: "variables-and-data-types",
            subtitle:
              "Learn what variables are, how Python stores ints, floats, strings and bools, and why choosing the right type matters.",
            objectives: [
              "Identify and use Python's fundamental data types (int, float, str, bool) and variables",
              "Apply expressions and assignment to store and update program state",
              "Explain why data typing matters for memory and computation",
            ],
            pdfPath: "/B2.1.1_Lesson_Storing_Values.pdf",
          },
          {
            title: "String Manipulation & Type Conversion",
            slug: "string-manipulation-and-type-conversion",
            subtitle:
              "Practice slicing and joining strings, then safely convert between numbers and text so data can move between the keyboard and maths.",
            objectives: [
              "Use indexing, slicing and builtin methods to manipulate strings",
              "Convert between strings and numeric types to prepare data for computation",
              "Trace simple programs that mix numeric and string operations",
            ],
            pdfPath: "/B2.1.1_Lesson_Storing_Values.pdf",
            locked: true,
          },
          {
            title: "Input/Output & Basic Debugging",
            slug: "input-output-and-basic-debugging",
            subtitle:
              "Use input() and print() to talk to your programs, and discover trace tables, try/except and print debugging to squash runtime errors.",
            objectives: [
              "Employ input() and print() for basic console interaction",
              "Construct trace tables and interpret Python tracebacks to debug code",
              "Handle runtime errors using try, except, and finally blocks",
            ],
            pdfPath: "/B2.1.1_Lesson_Storing_Values.pdf",
            locked: true,
          },
          {
            title: "Arithmetic Operators & Simple Programs",
            slug: "arithmetic-operators-and-simple-programs",
            subtitle:
              "Tie together arithmetic operators and the math module to write tiny calculators that solve everyday problems.",
            objectives: [
              "Combine arithmetic operators and builtin math functions in expressions",
              "Write small scripts that compute and display realworld quantities",
              "Evaluate programs for correctness using sample test data",
            ],
            pdfPath: "/B2.1.1_Lesson_Storing_Values.pdf",
            locked: true,
          },
        ],
      },
      {
        title: "Control Flow & Functions",
        slug: "control-flow-and-functions",
        locked: true,
        lessons: [
          {
            title: "Conditional Logic",
            slug: "conditional-logic",
            subtitle:
              "Control a program's path with if, elif and else so your code can make decisions just like you do.",
            objectives: [
              "Construct if, elif, else statements with relational and Boolean operators",
              "Trace branching code paths given various inputs",
              "Justify selection structures chosen to meet explicit requirements",
            ],
            locked: true,
          },
          {
            title: "For Loops & Iterable Processing",
            slug: "for-loops-and-iterable-processing",
            subtitle:
              "Harness for loops to repeat work over ranges, lists and strings while collecting useful results along the way.",
            objectives: [
              "Iterate over ranges, lists and strings with for loops",
              "Accumulate results with counters and aggregators inside a loop",
              "Predict the time complexity of counted loops in simple cases",
            ],
            locked: true,
          },
          {
            title: "While Loops & Loop Control",
            slug: "while-loops-and-loop-control",
            subtitle:
              "Build condition-controlled loops and learn break and continue so you can keep looping only as long as necessary.",
            objectives: [
              "Design conditioncontrolled loops that terminate correctly",
              "Use break and continue to refine loop behaviour",
              "Detect and prevent infinite loops through reasoning and testing",
            ],
            locked: true,
          },
          {
            title: "Functions & Modularisation",
            slug: "functions-and-modularisation",
            subtitle:
              "Package reusable code into functions, pass information with parameters and return clean answers to simplify growing programs.",
            objectives: [
              "Define and call functions with parameters and return values",
              "Differentiate local vs global scope and avoid sideeffects",
              "Refactor code into reusable modules for readability and testing",
            ],
            locked: true,
          },
        ],
      },
      {
        title: "Core Data Structures",
        slug: "core-data-structures",
        locked: true,
        lessons: [
          {
            title: "Lists & 1D Arrays",
            slug: "lists-and-1d-arrays",
            subtitle:
              "Master Python lists for ordered, mutable collections and see how they compare with fixed arrays in other languages.",
            objectives: [
              "Create, index, slice and mutate Python lists",
              "Use list methods (append, pop, sort) to manage dynamic data",
              "Compare Python lists (dynamic) with fixed-size static arrays and discuss trade-offs",
            ],
            locked: true,
          },
          {
            title: "2D Lists & Tabular Data",
            slug: "2d-lists-and-tabular-data",
            subtitle:
              "Represent grids and tables as nested lists and traverse them with nested loops to unlock simple matrix problems.",
            objectives: [
              "Represent matrices or tables as nested lists",
              "Traverse 2D structures with nested loops",
              "Choose between rowmajor and columnmajor traversal for tasks",
            ],
            locked: true,
          },
          {
            title: "Stacks & Queues Fundamentals",
            slug: "stacks-and-queues-fundamentals",
            subtitle:
              "Implement last-in-first-out and first-in-first-out structures, including peek/front, and apply them to real scenarios like undo and print queues.",
            objectives: [
              "Implement LIFO and FIFO structures with Python lists/deque",
              "Apply push/pop, enqueue/dequeue, and peek/front operations to problem scenarios",
              "Evaluate tradeoffs of arraybased vs linked implementations",
            ],
            locked: true,
          },
          {
            title: "Dictionaries & Sets",
            slug: "dictionaries-and-sets",
            subtitle:
              "Store data for instant lookup with dicts, and enforce uniqueness or perform set logic with Python sets while learning the basics of hashing.",
            objectives: [
              "Store keyvalue pairs in dicts for fast lookup",
              "Use sets to enforce uniqueness and perform union/intersection",
              "Explain hashing conceptually and its impact on performance",
            ],
            locked: true,
          },
        ],
      },
      {
        title: "Algorithms & Complexity",
        slug: "algorithms-and-complexity",
        locked: true,
        lessons: [
          {
            title: "Linear vs Binary Search",
            slug: "linear-vs-binary-search",
            subtitle:
              "Discover how to find information in a collection using both brute force and divide-and-conquer approaches, then measure their efficiency.",
            objectives: [
              "Trace and implement linear search and binary search algorithms",
              "Measure search efficiency using step counts and Big O notation",
              "Select an appropriate search strategy given data ordering",
            ],
            locked: true,
          },
          {
            title: "Bubble & Selection Sort",
            slug: "bubble-and-selection-sort",
            subtitle:
              "Step through two classic quadratic sorting methods to see exactly how swaps reorder a list and why performance suffers on big data.",
            objectives: [
              "Implement bubble and selection sort from pseudocode",
              "Compare their best, average and worstcase complexities",
              "Visualise how swaps reorder a list during each pass",
            ],
            locked: true,
          },
          {
            title: "Algorithm Analysis & Big O",
            slug: "algorithm-analysis-and-big-o",
            subtitle:
              "Learn to read code for growth rates, express efficiency in Big O notation and weigh speed against memory in design choices.",
            objectives: [
              "Derive Big O for sequences of statements, conditionals and loops",
              "Classify algorithms into constant, logarithmic, linear or quadratic time",
              "Discuss space-time tradeoffs when optimising code",
            ],
            locked: true,
          },
          {
            title: "Recursion Basics (HL Extension)",
            slug: "recursion-basics-(hl-extension)",
            subtitle:
              "Peek inside the call stack and write simple recursive solutions like factorial and Fibonacci, comparing them with iterative loops.",
            objectives: [
              "Explain the call stack and base/recursive cases conceptually",
              "Write simple recursive functions (factorial, Fibonacci, list sum)",
              "Compare recursive vs iterative solutions for clarity and efficiency",
            ],
            locked: true,
          },
        ],
      },
      {
        title: "File Handling & Integration",
        slug: "file-handling-and-integration",
        locked: true,
        lessons: [
          {
            title: "Reading Text Files",
            slug: "reading-text-files",
            subtitle:
              "Open and read external text files safely, line by line, turning raw text into useful Python data while handling IOError problems.",
            objectives: [
              "Open and iterate through text files safely with context managers",
              "Parse lines into useful data types for further processing",
              "Handle IOError exceptions gracefully",
            ],
            locked: true,
          },
          {
            title: "Writing & Appending Files",
            slug: "writing-and-appending-files",
            subtitle:
              "Create or extend files from Python, serialising data as CSV or JSON so you can save program state or export results.",
            objectives: [
              "Write and append records to text files",
              "Serialise lists/dicts as CSV or JSON strings",
              "Validate file output by inspecting contents programmatically",
            ],
            locked: true,
          },
          {
            title: "MiniProject Planning & Integration",
            slug: "miniproject-planning-and-integration",
            subtitle:
              "Design a small software project that integrates concepts from the course, turning a specification and data model into an incremental build plan.",
            objectives: [
              "Apply lists/dicts, functions and file I/O to design a cohesive program",
              "Create a specification, data model and algorithm outline for the project",
              "Break the project into iterative, testable milestones",
            ],
            locked: true,
          },
          {
            title: "Testing, Debugging & Documentation",
            slug: "testing-debugging-and-documentation",
            subtitle:
              "Finish by writing asserts, polishing docstrings and using systematic debugging to raise code quality to professional standards.",
            objectives: [
              "Develop test cases using assert statements or pytest",
              "Employ systematic debugging strategies to locate defects",
              "Produce docstrings and comments that meet IB academic integrity",
            ],
            locked: true,
          },
        ],
      },
    ],
  },
];
